import _ from "lodash";
import * as GQL from "src/types/graphql";

enum FormMessageType {
  Adhoc = "adhoc",
  Automatic = "form_status",
}

export enum OfferTransition {
  NewOffer = "NEW_OFFER",
  OfferPlaced = "OFFER_PLACED",
  OfferedToAccepted = "OFFERED_TO_ACCEPTED",
  OfferedToDeclined = "OFFERED_TO_DECLINED",
  OfferedToRevoked = "OFFERED_TO_REVOKED",
  AcceptedToDeclined = "ACCEPTED_TO_DECLINED",
  AcceptedToRevoked = "ACCEPTED_TO_REVOKED",
}

export enum WailistTransition {
  Wailisted = "WAITLISTED",
  WaitlistedToWithdrawn = "WAITLISTED_TO_WITHDRAWN",
  WaitlistedToRemoved = "WAITLISTED_TO_REMOVED",
  WithdrawnToRemoved = "WITHDRAWN_TO_REMOVED",
}

type MessageTransition = WailistTransition &
  OfferTransition &
  GQL.form_transition_enum;

export interface FormattedFormMessage {
  type: FormMessageType;
  messageType: GQL.message_type_enum;
  sentAt: string | null;
  subject?: string;
  body?: string;
  sender?: string | null;
  transition?: MessageTransition;
  recipient?: string | null;
  status: GQL.message_status_enum;
  contentId?: string | null;
}

function getMessageBody(
  messageType: GQL.message_type_enum,
  message: GQL.GetFormMessages_message_adhoc_form_receiver_message_adhoc | null
) {
  if (!message) {
    return undefined;
  }

  switch (messageType) {
    case GQL.message_type_enum.SMS:
      return message.message_adhoc_payload_sms?.message_body;
    case GQL.message_type_enum.email:
      return message.message_adhoc_payload_email?.message_body;
  }
}

// TODO: LOI - not sure if this logic is correct, but it works for now
export function toFormMessageList(
  formMessages: GQL.GetFormMessages
): FormattedFormMessage[] {
  /* As a quick fix, we are removing messages that were not really sent by trusting
    the message body with the correct contact information. */
  const filteredAdhocMessages = formMessages.message_adhoc_form_receiver.filter(
    ({ message_adhoc, receiver_person }) =>
      (message_adhoc?.message_adhoc_payload_email?.message_body &&
        receiver_person?.email_address) ||
      (message_adhoc?.message_adhoc_payload_sms?.message_body &&
        receiver_person?.phone_number)
  );

  const formattedAdhocMessages = filteredAdhocMessages.map(
    ({
      message_adhoc,
      message_type,
      sent_at,
      message_status,
      receiver_contact_information,
    }) => ({
      type: FormMessageType.Adhoc,
      messageType: message_type,
      sentAt: sent_at,
      subject: message_adhoc?.message_adhoc_payload_email?.message_subject,
      body: getMessageBody(message_type, message_adhoc),
      sender:
        message_adhoc?.sender_person?.full_name ??
        message_adhoc?.sender_person?.user?.name,
      transition: undefined,
      recipient: receiver_contact_information,
      status: message_status,
      contentId:
        message_adhoc?.message_adhoc_payload_rich_contents?.[0]?.content_id,
    })
  );

  const formattedFormStatusMessages = formMessages.message_form_status.map(
    (formStatusMessage) => ({
      type: FormMessageType.Automatic,
      messageType: formStatusMessage.message_type,
      sentAt: formStatusMessage.created_at,
      subject: formStatusMessage.message_subject ?? undefined,
      body: formStatusMessage.message_body ?? undefined,
      sender:
        formStatusMessage.form.form_template.enrollment_period.organization
          ?.name,
      transition: formStatusMessage.form_transition as MessageTransition,
      recipient: formStatusMessage.receiver_contact_information,
      status: formStatusMessage.message_status,
    })
  );

  const formatterdOfferStatusMessages = formMessages.message_offer_status.map(
    (offerStatusMessage) => ({
      type: FormMessageType.Automatic,
      messageType: offerStatusMessage.message_type,
      sentAt: offerStatusMessage.created_at,
      subject: offerStatusMessage.message_subject ?? undefined,
      body: offerStatusMessage.message_body ?? undefined,
      sender:
        offerStatusMessage.offer.form.form_template.enrollment_period
          .organization?.name,
      transition: offerStatusMessage.offer_transition as MessageTransition,
      recipient: offerStatusMessage.receiver_contact_information,
      status: offerStatusMessage.message_status,
    })
  );

  const formatterdWaitlistStatusMessages =
    formMessages.message_waitlist_status.map((waitlistStatusMessage) => ({
      type: FormMessageType.Automatic,
      messageType: waitlistStatusMessage.message_type,
      sentAt: waitlistStatusMessage.created_at,
      subject: waitlistStatusMessage.message_subject ?? undefined,
      body: waitlistStatusMessage.message_body ?? undefined,
      sender:
        waitlistStatusMessage.waitlist.form.form_template.enrollment_period
          .organization?.name,
      transition:
        waitlistStatusMessage.waitlist_transition as MessageTransition,
      recipient: waitlistStatusMessage.receiver_contact_information,
      status: waitlistStatusMessage.message_status,
    }));
  const formattedFormMessages = [
    ...formattedAdhocMessages,
    ...formattedFormStatusMessages,
    ...formatterdOfferStatusMessages,
    ...formatterdWaitlistStatusMessages,
  ];

  return _.sortBy(
    formattedFormMessages,
    (message) => message.sentAt || ""
  ).reverse();
}

export function getFormattedTransition(transition: MessageTransition) {
  if (!transition) {
    return undefined;
  }

  switch (transition) {
    case GQL.form_transition_enum.NEW_FORM:
      return { from: "New Form", to: undefined };
    case GQL.form_transition_enum.SUBMITTED_TO_ADMIN_CANCELLED:
      return { from: "Submitted", to: "Admin canceled" };
    case GQL.form_transition_enum.SUBMITTED_TO_CANCELLED:
      return { from: "Submitted", to: "Canceled" };
    case GQL.form_transition_enum.SUBMITTED_TO_DELETED:
      return { from: "Submitted", to: "Deleted" };
    case GQL.form_transition_enum.SUBMITTED_TO_IN_PROGRESS:
      return { from: "Submitted", to: "In progress" };
    case GQL.form_transition_enum.SUBMITTED_TO_VERIFIED:
      return { from: "Submitted", to: "Verified" };
    case GQL.form_transition_enum.IN_PROGRESS_TO_ADMIN_CANCELLED:
      return { from: "In progress", to: "Admin canceled" };
    case GQL.form_transition_enum.IN_PROGRESS_TO_CANCELLED:
      return { from: "In progress", to: "Canceled" };
    case GQL.form_transition_enum.IN_PROGRESS_TO_DELETED:
      return { from: "In progress", to: "Deleted" };
    case GQL.form_transition_enum.IN_PROGRESS_TO_SUBMITTED:
      return { from: "In progress", to: "Submitted" };
    case GQL.form_transition_enum.IN_PROGRESS_TO_VERIFIED:
      return { from: "In progress", to: "Verified" };
    case GQL.form_transition_enum.CANCELLED_TO_ADMIN_CANCELLED:
      return { from: "Canceled", to: "Admin canceled" };
    case GQL.form_transition_enum.CANCELLED_TO_DELETED:
      return { from: "Canceled", to: "Deleted" };
    case GQL.form_transition_enum.CANCELLED_TO_IN_PROGRESS:
      return { from: "Canceled", to: "In progress" };
    case GQL.form_transition_enum.CANCELLED_TO_SUBMITTED:
      return { from: "Canceled", to: "Submitted" };
    case GQL.form_transition_enum.CANCELLED_TO_VERIFIED:
      return { from: "Canceled", to: "Verified" };
    case OfferTransition.NewOffer:
      return { from: "New Offer", to: undefined };
    case OfferTransition.OfferPlaced:
      return { from: "Offer placed", to: undefined };
    case OfferTransition.OfferedToAccepted:
      return { from: "Offered", to: "Accepted" };
    case OfferTransition.OfferedToDeclined:
      return { from: "Offered", to: "Declined" };
    case OfferTransition.OfferedToRevoked:
      return { from: "Offered", to: "Revoked" };
    case OfferTransition.AcceptedToDeclined:
      return { from: "Accepted", to: "Declined" };
    case OfferTransition.AcceptedToRevoked:
      return { from: "Accepted", to: "Revoked" };
    case WailistTransition.Wailisted:
      return { from: "Waitlisted", to: undefined };
    case WailistTransition.WaitlistedToWithdrawn:
      return { from: "Waitlisted", to: "Withdrawn" };
    case WailistTransition.WaitlistedToRemoved:
      return { from: "Waitlisted", to: "Removed" };
    case WailistTransition.WithdrawnToRemoved:
      return { from: "Withdrawn", to: "Removed" };
    case GQL.form_transition_enum.LOTTERY_READY_TO_VERIFIED:
      return { from: "Lottery ready", to: "Verified" };
    default:
      return undefined;
  }
}
